import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, axiosInstance as axios } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  ChevronLeft, Printer, Download, Calendar, BookOpen, Trophy, 
  Camera, Sparkles, Heart, Star, Gift, Flag
} from 'lucide-react';
import { toast } from 'sonner';

const API = '';

export default function TimelineExport() {
  const { familyId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const token = localStorage.getItem('session_token');
  const printRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [timelineData, setTimelineData] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedYear, setSelectedYear] = useState(searchParams.get('year') || 'all');
  const [selectedType, setSelectedType] = useState(searchParams.get('type') || 'all');
  const [showPhotos, setShowPhotos] = useState(true);

  const fetchTimeline = useCallback(async () => {
    try {
      const [timelineRes, statsRes] = await Promise.all([
        axios.get(`${API}/api/families/${familyId}/timeline?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/api/families/${familyId}/timeline/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setTimelineData(timelineRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch timeline:', error);
      toast.error('Failed to load timeline');
    } finally {
      setLoading(false);
    }
  }, [familyId, token]);

  useEffect(() => {
    fetchTimeline();
  }, [fetchTimeline]);

  const handlePrint = () => {
    window.print();
  };

  const filteredItems = () => {
    if (!timelineData?.items) return [];
    
    let items = timelineData.items;
    
    if (selectedYear !== 'all') {
      items = items.filter(item => {
        const itemYear = item.date ? item.date.substring(0, 4) : null;
        return itemYear === selectedYear;
      });
    }
    
    if (selectedType !== 'all') {
      items = items.filter(item => item.type === selectedType);
    }
    
    return items;
  };

  const getTypeLabel = (type) => {
    const labels = {
      'event': 'Event',
      'story': 'Story',
      'milestone': 'Milestone',
      'photo': 'Photo',
      'prayer_answered': 'Answered Prayer'
    };
    return labels[type] || type;
  };

  const getTypeEmoji = (type) => {
    const emojis = {
      'event': '📅',
      'story': '📖',
      'milestone': '🏆',
      'photo': '📷',
      'prayer_answered': '✨'
    };
    return emojis[type] || '📌';
  };

  const getCategoryEmoji = (category) => {
    const emojis = {
      'childhood': '👶',
      'traditions': '🎄',
      'achievements': '🏆',
      'challenges': '💪',
      'values': '💡',
      'wisdom': '📚',
      'recipes': '🍳',
      'other': '📖'
    };
    return emojis[category] || '📖';
  };

  // Group items by year
  const groupedByYear = () => {
    const items = filteredItems();
    const grouped = {};
    
    items.forEach(item => {
      const year = item.date ? item.date.substring(0, 4) : 'Undated';
      if (!grouped[year]) {
        grouped[year] = [];
      }
      grouped[year].push(item);
    });
    
    // Sort years descending
    const sortedYears = Object.keys(grouped).sort((a, b) => {
      if (a === 'Undated') return 1;
      if (b === 'Undated') return -1;
      return b.localeCompare(a);
    });
    
    return { grouped, sortedYears };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-amber-500"></div>
      </div>
    );
  }

  const { grouped, sortedYears } = groupedByYear();
  const items = filteredItems();

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { 
            background: white !important; 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .print-page { 
            page-break-inside: avoid;
            margin: 0;
            padding: 20px;
          }
          .print-item {
            page-break-inside: avoid;
            margin-bottom: 16px;
          }
          @page {
            margin: 0.5in;
            size: letter portrait;
          }
        }
        @media screen {
          .print-only { display: none; }
        }
      `}</style>

      {/* Screen Controls - Hidden during print */}
      <div className="no-print bg-slate-900 text-white sticky top-0 z-50 border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(`/family/${familyId}/timeline`)}
                className="text-slate-400 hover:text-white"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to Timeline
              </Button>
              <h1 className="text-lg font-semibold">Export Timeline</h1>
            </div>
            <div className="flex items-center gap-3">
              <select 
                value={selectedYear}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm"
              >
                <option value="all">All Years</option>
                {timelineData?.available_years?.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
              <select 
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="bg-slate-800 border border-slate-600 rounded px-3 py-1.5 text-sm"
              >
                <option value="all">All Types</option>
                <option value="event">Events</option>
                <option value="story">Stories</option>
                <option value="milestone">Milestones</option>
                <option value="photo">Photos</option>
                <option value="prayer_answered">Answered Prayers</option>
              </select>
              <label className="flex items-center gap-2 text-sm">
                <input 
                  type="checkbox"
                  checked={showPhotos}
                  onChange={(e) => setShowPhotos(e.target.checked)}
                  className="rounded"
                />
                Include Photos
              </label>
              <Button onClick={handlePrint} className="bg-amber-600 hover:bg-amber-700">
                <Printer className="h-4 w-4 mr-2" />
                Print / Save PDF
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Printable Content */}
      <div ref={printRef} className="print-page bg-white min-h-screen">
        <div className="max-w-4xl mx-auto px-8 py-8">
          {/* Header */}
          <div className="text-center mb-8 border-b-2 border-amber-500 pb-6">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">
              {timelineData?.family_name} Family Legacy
            </h1>
            <p className="text-slate-600">A Journey Through Our Memories & Milestones</p>
            <div className="flex justify-center gap-8 mt-4 text-sm text-slate-500">
              <span><strong className="text-amber-600">{items.length}</strong> Moments</span>
              <span><strong className="text-blue-600">{stats?.by_type?.events || 0}</strong> Events</span>
              <span><strong className="text-green-600">{stats?.by_type?.stories || 0}</strong> Stories</span>
              <span><strong className="text-purple-600">{stats?.members_count || 0}</strong> Members</span>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Generated on {new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          {/* Timeline Content */}
          {sortedYears.map(year => (
            <div key={year} className="mb-8">
              {/* Year Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="bg-amber-500 text-white px-4 py-1 rounded-full font-bold text-lg">
                  {year}
                </div>
                <div className="flex-1 h-0.5 bg-amber-200"></div>
                <span className="text-sm text-slate-400">
                  {grouped[year].length} {grouped[year].length === 1 ? 'moment' : 'moments'}
                </span>
              </div>

              {/* Items for this year */}
              <div className="space-y-4 pl-4 border-l-2 border-amber-200">
                {grouped[year].map((item, idx) => (
                  <div key={`${item.type}-${item.id}`} className="print-item relative pl-6">
                    {/* Timeline dot */}
                    <div className="absolute -left-[9px] top-2 w-4 h-4 rounded-full bg-white border-2 border-amber-400"></div>
                    
                    {/* Card */}
                    <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className="text-lg mr-2">{getTypeEmoji(item.type)}</span>
                          <span className="font-semibold text-slate-800">{item.title}</span>
                          {item.metadata?.category && (
                            <span className="ml-2 text-sm text-slate-500">
                              {getCategoryEmoji(item.metadata.category)} {item.metadata.category}
                            </span>
                          )}
                        </div>
                        <div className="text-right text-sm text-slate-500">
                          <p className="font-medium">{item.date_display}</p>
                          {item.author_name && <p className="text-xs">by {item.author_name}</p>}
                        </div>
                      </div>

                      {item.description && (
                        <p className="text-slate-600 text-sm mb-2 whitespace-pre-wrap">
                          {item.description}
                        </p>
                      )}

                      {/* Metadata */}
                      <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                        {item.location && (
                          <span>📍 {item.location}</span>
                        )}
                        {item.metadata?.featured_family_member && (
                          <span className="text-amber-600">
                            About: {item.metadata.featured_family_member}
                            {item.metadata.time_period && ` (${item.metadata.time_period})`}
                          </span>
                        )}
                        {item.metadata?.likes > 0 && (
                          <span>❤️ {item.metadata.likes} likes</span>
                        )}
                        {item.metadata?.prayer_count > 0 && (
                          <span>🙏 {item.metadata.prayer_count} prayed</span>
                        )}
                        {item.metadata?.tags?.length > 0 && (
                          <span>🏷️ {item.metadata.tags.join(', ')}</span>
                        )}
                      </div>

                      {/* Photo thumbnail */}
                      {showPhotos && item.type === 'photo' && item.metadata?.file_url && (
                        <div className="mt-3">
                          <img 
                            src={item.metadata.file_url} 
                            alt={item.title}
                            className="max-w-xs rounded border"
                            style={{ maxHeight: '150px', objectFit: 'cover' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-slate-200 text-center text-sm text-slate-400">
            <p>Generated with Legacy Forge Family - Building Family Legacies Together</p>
            <p className="mt-1">© {new Date().getFullYear()} {timelineData?.family_name} Family</p>
          </div>
        </div>
      </div>
    </>
  );
}
